// src/pages/StorageRulesTest.js
import React, { useState, useEffect } from 'react';
import { getStorage, ref, uploadString, getDownloadURL, listAll } from 'firebase/storage';
import { getAuth, signInWithEmailAndPassword, signOut } from 'firebase/auth';

const StorageRulesTest = () => {
  const [logs, setLogs] = useState([]);
  const [authState, setAuthState] = useState('unknown');
  const [currentUser, setCurrentUser] = useState(null);

  const addLog = (message) => {
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
    console.log(message);
  };

  useEffect(() => {
    const auth = getAuth();
    if (auth.currentUser) {
      setAuthState('logged-in');
      setCurrentUser(auth.currentUser);
      addLog(`Already logged in as: ${auth.currentUser.email}`);
    } else {
      setAuthState('logged-out');
      addLog('Not logged in');
    }
  }, []);

  const login = async () => {
    try {
      addLog('Attempting to login as jane.doe@cooc.com.au...');
      const auth = getAuth();
      const credential = await signInWithEmailAndPassword(auth, 'jane.doe@cooc.com.au', 'test123');
      setAuthState('logged-in');
      setCurrentUser(credential.user);
      addLog(`Logged in as: ${credential.user.email}`);
    } catch (error) {
      addLog(`Login error: ${error.message}`);
    }
  };

  const logout = async () => {
    try {
      addLog('Logging out...');
      const auth = getAuth();
      await signOut(auth);
      setAuthState('logged-out');
      setCurrentUser(null);
      addLog('Logged out');
    } catch (error) {
      addLog(`Logout error: ${error.message}`);
    }
  };

  const testPublicUpload = async () => {
    try {
      addLog('Testing upload to public location...');
      const storage = getStorage();
      const testRef = ref(storage, `public-test/${Date.now()}.txt`);
      
      await uploadString(testRef, 'This is a test file');
      const url = await getDownloadURL(testRef);
      
      addLog('✅ Public upload successful!');
      addLog(`URL: ${url}`);
    } catch (error) {
      addLog(`❌ Public upload failed: ${error.message}`);
    }
  };

  const testUserUpload = async () => {
    try {
      addLog('Testing upload to user-specific location...');
      const storage = getStorage();
      const userEmail = currentUser?.email || 'jane.doe@cooc.com.au';
      const testRef = ref(storage, `Users/${userEmail}/test/${Date.now()}.txt`);
      
      await uploadString(testRef, 'This is a user-specific test file');
      const url = await getDownloadURL(testRef);
      
      addLog('✅ User-specific upload successful!');
      addLog(`URL: ${url}`);
    } catch (error) {
      addLog(`❌ User-specific upload failed: ${error.message}`);
    }
  };

  const testListFiles = async () => {
    try {
      addLog('Testing listing files...');
      const storage = getStorage();
      const userEmail = currentUser?.email || 'jane.doe@cooc.com.au';
      const listRef = ref(storage, `Users/${userEmail}`);
      
      const result = await listAll(listRef);
      
      addLog(`✅ Found ${result.prefixes.length} folders and ${result.items.length} files`);
      result.prefixes.forEach(folderRef => {
        addLog(`Folder: ${folderRef.fullPath}`);
      });
      result.items.forEach(itemRef => {
        addLog(`File: ${itemRef.fullPath}`);
      });
    } catch (error) {
      addLog(`❌ Listing files failed: ${error.message}`);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto', fontFamily: 'Arial, sans-serif' }}>
      <h1 style={{ marginBottom: '20px' }}>Firebase Storage Rules Test</h1>
      <p style={{ marginBottom: '20px' }}>
        This utility tests Firebase Storage rules to help identify permission issues.
      </p>
      
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '18px', marginBottom: '15px' }}>Authentication</h2>
        <div style={{ 
          backgroundColor: authState === 'logged-in' ? '#e8f5e9' : '#ffebee',
          padding: '10px', 
          borderRadius: '4px',
          marginBottom: '15px'
        }}>
          <strong>Status:</strong> {authState === 'logged-in' ? 'Logged In' : 'Logged Out'}
          {currentUser && <div><strong>User:</strong> {currentUser.email}</div>}
        </div>
        
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            onClick={login} 
            disabled={authState === 'logged-in'}
            style={{
              backgroundColor: authState === 'logged-in' ? '#ccc' : '#128C7E',
              color: 'white',
              border: 'none',
              padding: '10px 15px',
              borderRadius: '4px',
              cursor: authState === 'logged-in' ? 'not-allowed' : 'pointer'
            }}
          >
            Login as Jane Doe
          </button>
          
          <button 
            onClick={logout} 
            disabled={authState !== 'logged-in'}
            style={{
              backgroundColor: authState !== 'logged-in' ? '#ccc' : '#ff5722',
              color: 'white',
              border: 'none',
              padding: '10px 15px',
              borderRadius: '4px',
              cursor: authState !== 'logged-in' ? 'not-allowed' : 'pointer'
            }}
          >
            Logout
          </button>
        </div>
      </div>
      
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '18px', marginBottom: '15px' }}>Storage Tests</h2>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button 
            onClick={testPublicUpload}
            style={{
              backgroundColor: '#2196f3',
              color: 'white',
              border: 'none',
              padding: '10px 15px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Test Public Upload
          </button>
          
          <button 
            onClick={testUserUpload}
            style={{
              backgroundColor: '#4caf50',
              color: 'white',
              border: 'none',
              padding: '10px 15px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Test User-Specific Upload
          </button>
          
          <button 
            onClick={testListFiles}
            style={{
              backgroundColor: '#9c27b0',
              color: 'white',
              border: 'none',
              padding: '10px 15px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Test List Files
          </button>
        </div>
      </div>
      
      <div style={{
        backgroundColor: '#f5f5f5',
        padding: '15px',
        borderRadius: '4px'
      }}>
        <h3 style={{ marginTop: '0', marginBottom: '10px' }}>Test Logs</h3>
        <div style={{
          backgroundColor: '#000',
          color: '#0f0',
          fontFamily: 'monospace',
          padding: '10px',
          borderRadius: '4px',
          maxHeight: '400px',
          overflow: 'auto'
        }}>
          {logs.length === 0 ? (
            <p>No logs yet. Run a test to see logs.</p>
          ) : (
            logs.map((log, index) => (
              <div key={index}>{log}</div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default StorageRulesTest;